import React, { useState, useEffect, useCallback } from 'react';
import { projectService } from '../../services/projectService';
import { generateThumbnail } from '../../services/thumbnailService';
import './ProjectForm.css';

const ProjectForm = ({ project, onClose, onRefresh }) => {
  const [changesSaved, setChangesSaved] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    longDescription: '',
    tags: [],
    imageUrl: '',
    thumbnailUrl: '',
    additionalImages: [],
    videoUrls: [],
    demoUrl: '',
    sourceUrl: '',
    websiteUrl: '',
    featured: false,
    isDraft: false,
    projectDate: ''
  });
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [uploadingVideos, setUploadingVideos] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [existingTags, setExistingTags] = useState([]);


  // Fetch all existing tags from all projects
  useEffect(() => {
    const fetchExistingTags = async () => {
      try {
        const projects = await projectService.getAllProjects();
        const allTags = new Set();
        projects.forEach(proj => {
          if (proj.tags && Array.isArray(proj.tags)) {
            proj.tags.forEach(tag => allTags.add(tag));
          }
        });
        setExistingTags(Array.from(allTags).sort());
      } catch (error) {
        console.error('Error fetching existing tags:', error);
      }
    };
    fetchExistingTags();
  }, []);

  useEffect(() => {
    const initialData = {
      id: project?.id || '',
      title: project?.title || '',
      description: project?.description || '',
      longDescription: project?.longDescription || '',
      tags: project?.tags || [],
      imageUrl: project?.imageUrl || '',
      thumbnailUrl: project?.thumbnailUrl || '',
      additionalImages: project?.additionalImages || [],
      videoUrls: project?.videoUrls || [],
      demoUrl: project?.demoUrl || '',
      sourceUrl: project?.sourceUrl || '',
      websiteUrl: project?.websiteUrl || '',
      featured: project?.featured || false,
      isDraft: project?.isDraft || false,
      projectDate: project?.projectDate || ''
    };

    setFormData(initialData);
    setInitialFormData(initialData);
  }, [project]);

  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormData) return false;
    
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);


  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (hasUnsavedChanges()) {
          const shouldSave = window.confirm(
            'You have unsaved changes. Do you want to save before closing?\n\n' +
            'Click "OK" to save and close, or "Cancel" to close without saving.'
          );
          
          if (shouldSave) {
            document.getElementById('project-form').requestSubmit();
            return;
          }
        }
        onClose(changesSaved);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, changesSaved, hasUnsavedChanges]);

  const handleInputChange = (field, value) => {
    // Special validation for project ID - only letters allowed
    if (field === 'id') {
      // Remove any non-letter characters
      value = value.replace(/[^a-zA-Z]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleClose = () => {
    if (hasUnsavedChanges()) {
      const shouldSave = window.confirm(
        'You have unsaved changes. Do you want to save before closing?\n\n' +
        'Click "OK" to save and close, or "Cancel" to close without saving.'
      );
      
      if (shouldSave) {
        // Trigger form submission
        document.getElementById('project-form').requestSubmit();
        return;
      }
    }
    
    onClose(changesSaved);
  };

  const handleAddTag = (tagToAdd) => {
    const tag = tagToAdd || newTag.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setNewTag('');
    }
  };

  const handleAddExistingTag = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };


  const handleRemoveImage = async (index) => {
    const imageToRemove = formData.additionalImages[index];

    // Delete from Firebase Storage (both full image and thumbnail)
    if (imageToRemove && imageToRemove.src) {
      try {
        await projectService.deleteFileByUrl(imageToRemove.src);
      } catch (error) {
        console.error('Error deleting image from storage:', error);
      }
    }
    if (imageToRemove && imageToRemove.thumbnailSrc) {
      try {
        await projectService.deleteFileByUrl(imageToRemove.thumbnailSrc);
      } catch (error) {
        console.error('Error deleting thumbnail from storage:', error);
      }
    }

    setFormData(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateImageCaption = (index, newCaption) => {
    setFormData(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.map((img, i) => 
        i === index 
          ? { ...img, caption: newCaption || undefined }
          : img
      )
    }));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.parentNode);
    
    // Set the entire container as the drag image for better visual feedback
    const container = e.target.closest('.image-item');
    if (container) {
      e.dataTransfer.setDragImage(container, 100, 60);
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...formData.additionalImages];
    const draggedImage = newImages[draggedIndex];
    
    // Remove dragged item
    newImages.splice(draggedIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);

    setFormData(prev => ({
      ...prev,
      additionalImages: newImages
    }));

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };


  const handleRemoveVideo = async (videoToRemove) => {
    // Delete from Firebase Storage
    if (videoToRemove) {
      try {
        await projectService.deleteFileByUrl(videoToRemove);
      } catch (error) {
        console.error('Error deleting video from storage:', error);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      videoUrls: prev.videoUrls.filter(video => video !== videoToRemove)
    }));
  };

  const handleMainImageUpload = async (file) => {
    if (!formData.id) {
      alert('Please save the project first to upload files');
      return;
    }

    setUploadingMain(true);
    try {
      // Delete old main image and thumbnail if they exist
      if (formData.imageUrl) {
        try {
          await projectService.deleteFileByUrl(formData.imageUrl);
        } catch (error) {
          console.error('Error deleting old main image:', error);
        }
      }
      if (formData.thumbnailUrl) {
        try {
          await projectService.deleteFileByUrl(formData.thumbnailUrl);
        } catch (error) {
          console.error('Error deleting old thumbnail:', error);
        }
      }

      const fileName = `main_${Date.now()}.${file.name.split('.').pop()}`;

      // Upload original image
      const uploadedUrl = await projectService.uploadImage(file, formData.id, fileName);

      // Generate and upload thumbnail
      let thumbnailUrl = null;
      try {
        const thumbnailBlob = await generateThumbnail(file);
        thumbnailUrl = await projectService.uploadThumbnail(thumbnailBlob, formData.id, fileName);
      } catch (thumbError) {
        console.error('Error generating thumbnail:', thumbError);
        // Continue without thumbnail
      }

      setFormData(prev => ({
        ...prev,
        imageUrl: uploadedUrl,
        thumbnailUrl: thumbnailUrl
      }));
      alert('Main image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading main image:', error);
      alert('Error uploading main image: ' + error.message);
    } finally {
      setUploadingMain(false);
    }
  };

  const handleAdditionalImagesUpload = async (files) => {
    if (!formData.id) {
      alert('Please save the project first to upload files');
      return;
    }

    setUploadingAdditional(true);
    try {
      // Upload images with thumbnails
      const uploadResults = await projectService.batchUploadImagesWithThumbnails(files, formData.id);
      const newImages = uploadResults.map((result, index) => ({
        src: result.imageUrl,
        thumbnailSrc: result.thumbnailUrl,
        caption: `Image ${formData.additionalImages.length + index + 1}`
      }));
      setFormData(prev => ({
        ...prev,
        additionalImages: [...prev.additionalImages, ...newImages]
      }));
      alert('Additional images uploaded successfully!');
    } catch (error) {
      console.error('Error uploading additional images:', error);
      alert('Error uploading additional images: ' + error.message);
    } finally {
      setUploadingAdditional(false);
    }
  };

  const handleVideosUpload = async (files) => {
    if (!formData.id) {
      alert('Please save the project first to upload files');
      return;
    }

    setUploadingVideos(true);
    try {
      const uploadedUrls = await projectService.batchUploadFiles(files, formData.id, 'videos');
      setFormData(prev => ({
        ...prev,
        videoUrls: [...prev.videoUrls, ...uploadedUrls]
      }));
      alert('Videos uploaded successfully!');
    } catch (error) {
      console.error('Error uploading videos:', error);
      alert('Error uploading videos: ' + error.message);
    } finally {
      setUploadingVideos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (project) {
        // Update existing project
        await projectService.updateProject(project.id, formData);
        alert('Project updated successfully!');
      } else {
        // Create new project
        if (!formData.id) {
          alert('Please provide a project ID');
          setIsSubmitting(false);
          return;
        }
        await projectService.createProjectWithId(formData.id, formData);
        alert('Project created successfully!');
      }
      setChangesSaved(true);
      onRefresh(); // Refresh the project list
      onClose(true); // Pass true to indicate changes were saved
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleDelete = async () => {
    if (!project) return; // Can't delete a new project
    
    if (window.confirm(`Are you sure you want to delete "${project.title}"? This action cannot be undone.`)) {
      setDeletingProject(true);
      try {
        // Delete all associated files from Firebase Storage before deleting the project
        const filesToDelete = [];
        
        // Add main image
        if (project.imageUrl) {
          filesToDelete.push(project.imageUrl);
        }
        
        // Add additional images
        if (project.additionalImages && project.additionalImages.length > 0) {
          project.additionalImages.forEach(img => {
            if (img.src) {
              filesToDelete.push(img.src);
            }
          });
        }
        
        // Add videos
        if (project.videoUrls && project.videoUrls.length > 0) {
          filesToDelete.push(...project.videoUrls);
        }
        
        // Delete all files from storage (in parallel)
        const deletePromises = filesToDelete.map(url => 
          projectService.deleteFileByUrl(url).catch(error => 
            console.error('Error deleting file:', url, error)
          )
        );
        
        await Promise.all(deletePromises);
        
        // Delete the project document
        await projectService.deleteProject(project.id);
        alert('Project and all associated files deleted successfully!');
        setChangesSaved(true);
        onRefresh();
        onClose(true); // Pass true to indicate changes were made
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Error deleting project: ' + error.message);
      } finally {
        setDeletingProject(false);
      }
    }
  };

  return (
    <div className="project-form">
      <div className="form-header">
        <div className="header-left">
          <h2>{project ? 'Edit Project' : 'Add New Project'}</h2>
          {project && project.isDraft && (
            <div className="draft-indicator">
              <span className="draft-badge editing">Editing Draft Project</span>
            </div>
          )}
        </div>
        <button onClick={handleClose} className="close-button">×</button>
      </div>

      <form onSubmit={handleSubmit} className="form-content" id="project-form">
        <div className="form-group">
          <label>Project ID *</label>
          <div className="input-with-button">
            <input
              type="text"
              value={formData.id}
              onChange={(e) => handleInputChange('id', e.target.value)}
              disabled={!!project}
              required
              pattern="[a-zA-Z]+"
              title="Only letters are allowed (no spaces, numbers, or special characters)"
              placeholder="e.g., myAwesomeProject"
            />
            <button
              type="button"
              onClick={() => handleInputChange('featured', !formData.featured)}
              className={`btn featured-toggle-btn ${formData.featured ? 'featured' : ''}`}
            >
              {formData.featured ? '★ Featured' : '☆ Not Featured'}
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('isDraft', !formData.isDraft)}
              className={`btn draft-toggle-btn ${formData.isDraft ? 'draft' : ''}`}
            >
              {formData.isDraft ? '📄 Draft' : '🌐 Published'}
            </button>
          </div>
          {project ? (
            <small>ID cannot be changed for existing projects</small>
          ) : (
            <small>Only letters allowed (no spaces, numbers, or special characters)</small>
          )}
        </div>

        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
            placeholder="Project title"
          />
        </div>

        <div className="form-group">
          <label>Project Date *</label>
          <input
            type="date"
            value={formData.projectDate}
            onChange={(e) => handleInputChange('projectDate', e.target.value)}
            required
            placeholder="Select project date"
          />
          <small>This date will be displayed throughout the website</small>
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            required
            rows="3"
            placeholder="Brief project description"
          />
        </div>

        <div className="form-group">
          <label>Long Description</label>
          <textarea
            value={formData.longDescription}
            onChange={(e) => handleInputChange('longDescription', e.target.value)}
            rows="8"
            placeholder="Detailed project description"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Main Image</label>
            {formData.imageUrl ? (
              <div className="main-image-preview">
                <img 
                  src={formData.imageUrl} 
                  alt="Main project preview"
                />
                <button 
                  type="button" 
                  onClick={async () => {
                    // Delete from Firebase Storage when removing main image
                    if (formData.imageUrl) {
                      try {
                        await projectService.deleteFileByUrl(formData.imageUrl);
                      } catch (error) {
                        console.error('Error deleting main image from storage:', error);
                      }
                    }
                    handleInputChange('imageUrl', '');
                  }}
                  className="btn remove-button"
                >
                  ×
                </button>
                <div className="image-actions">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files[0] && handleMainImageUpload(e.target.files[0])}
                    disabled={uploadingMain || !formData.id}
                    id="main-image-upload"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="main-image-upload" className={`btn upload-button ${uploadingMain ? 'uploading' : ''}`}>
                    {uploadingMain ? 'Uploading...' : 'Change Image'}
                  </label>
                </div>
              </div>
            ) : (
              <div className="upload-placeholder">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleMainImageUpload(e.target.files[0])}
                  disabled={uploadingMain || !formData.id}
                  id="main-image-upload-empty"
                  style={{ display: 'none' }}
                />
                <label htmlFor="main-image-upload-empty" className={`btn upload-button large ${uploadingMain ? 'uploading' : ''}`}>
                  {uploadingMain ? 'Uploading...' : 'Upload Main Image'}
                </label>
                {!formData.id && <p className="upload-note">Save project first to upload images</p>}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Tags</label>
            <div className="tags-input">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add a tag or select from existing tags below"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <button type="button" onClick={() => handleAddTag()} className="btn">Add</button>
            </div>
            <div className="tags-list">
              {formData.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag)} className="btn">×</button>
                </span>
              ))}
            </div>
            {existingTags.length > 0 && (
              <div className="existing-tags-section">
                <label className="existing-tags-label">Available tags (click to add):</label>
                <div className="existing-tags-list">
                  {existingTags
                    .filter(tag => !formData.tags.includes(tag))
                    .map((tag, index) => (
                      <span
                        key={index}
                        className="existing-tag"
                        onClick={() => handleAddExistingTag(tag)}
                      >
                        + {tag}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Additional Images</label>
          <div className="images-list">
            {formData.additionalImages.map((img, index) => (
              <div 
                key={index} 
                className={`image-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                draggable={false}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="drag-handle">⋮⋮</div>
                <img 
                  src={img.src} 
                  alt={img.caption || `Image ${index + 1}`} 
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, index)}
                />
                <button type="button" onClick={() => handleRemoveImage(index)} className="btn remove-image-btn">
                  ×
                </button>
                <div className="image-info">
                  <div className="image-caption-edit">
                    <input
                      type="text"
                      value={img.caption || ''}
                      onChange={(e) => handleUpdateImageCaption(index, e.target.value)}
                      placeholder="Enter caption..."
                      className="caption-input"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="upload-section">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => e.target.files.length > 0 && handleAdditionalImagesUpload(Array.from(e.target.files))}
              disabled={uploadingAdditional || !formData.id}
              id="additional-images-upload"
              style={{ display: 'none' }}
            />
            <label htmlFor="additional-images-upload" className={`btn upload-button ${uploadingAdditional ? 'uploading' : ''}`}>
              {uploadingAdditional ? 'Uploading...' : 'Upload Additional Images'}
            </label>
            {!formData.id && <p className="upload-note">Save project first to upload images</p>}
          </div>
        </div>

        <div className="form-group">
          <label>Videos</label>
          <div className="videos-list">
            {formData.videoUrls.map((video, index) => (
              <div key={index} className="video-item">
                <div className="video-preview">
                  <video src={video} muted preload="metadata" />
                  <div className="video-overlay">
                    <span>Video {index + 1}</span>
                  </div>
                </div>
                <button type="button" onClick={() => handleRemoveVideo(video)} className="btn remove-video-btn">
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="upload-section">
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => e.target.files.length > 0 && handleVideosUpload(Array.from(e.target.files))}
              disabled={uploadingVideos || !formData.id}
              id="videos-upload"
              style={{ display: 'none' }}
            />
            <label htmlFor="videos-upload" className={`btn upload-button ${uploadingVideos ? 'uploading' : ''}`}>
              {uploadingVideos ? 'Uploading...' : 'Upload Videos'}
            </label>
            {!formData.id && <p className="upload-note">Save project first to upload videos</p>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Embedded Video URL</label>
            <input
              type="url"
              value={formData.demoUrl}
              onChange={(e) => handleInputChange('demoUrl', e.target.value)}
              placeholder="https://www.youtube.com/embed/your_video_id"
            />
          </div>
          <div className="form-group">
            <label>Source Code URL</label>
            <input
              type="url"
              value={formData.sourceUrl}
              onChange={(e) => handleInputChange('sourceUrl', e.target.value)}
              placeholder="https://github.com/user/repo"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Website URL</label>
          <input
            type="url"
            value={formData.websiteUrl}
            onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
            placeholder="https://example.com"
          />
          <small>Link to the project's website or live demo</small>
        </div>


        <div className="form-actions">
          <button type="button" onClick={handleClose} className="btn cancel-button">
            Cancel
          </button>
          {project && (
            <button 
              type="button" 
              onClick={handleDelete} 
              disabled={deletingProject || isSubmitting}
              className="btn btn-danger delete-button"
            >
              {deletingProject ? 'Deleting...' : 'Delete Project'}
            </button>
          )}
          <button type="submit" disabled={isSubmitting} className="btn save-button">
            {isSubmitting ? 'Saving...' : (project ? 'Update Project' : 'Create Project')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;